// Tolerant readers for Core responses whose exact shape is not yet confirmed (contract A.3.2).
// They return null when no list is recognisable, so views can show an honest "format unknown" state.
import { text, participationState, isActivityCancelled, own, hasOwn, lookup } from "./memberDerive";

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

function firstList(d, keys) {
  if (Array.isArray(d)) return d;
  if (!isObj(d)) return null;
  const k = keys.find((x) => Array.isArray(own(d, x)));
  return k ? d[k] : null;
}

// Local paths only: no protocol-relative, backslash, whitespace or control characters.
const internalLink = (v) => (typeof v === "string" && /^\/(?!\/)[^\\\s\u0000-\u001f\u007f]*$/.test(v) ? v : null);

// Notification target from the Core ref: only known types with an own, non-empty scalar id; the target page re-checks rights.
const REF_ROUTE = { activity: "/aktivitaeten/", group: "/kreise/" };

function refLink(ref) {
  if (!isObj(ref)) return null;
  if (own(ref, "type") === "plan") {
    const activity = own(ref, "id"), owner = own(ref, "owner_id");
    return typeof activity === "string" && /^[a-f0-9]{24}$/.test(activity) && typeof owner === "string" && /^[a-f0-9]{24}$/.test(owner)
      ? `/plaene/${activity}/geteilt/${owner}` : null;
  }
  const route = typeof own(ref, "type") === "string" && Object.prototype.hasOwnProperty.call(REF_ROUTE, ref.type) ? REF_ROUTE[ref.type] : null;
  const id = own(ref, "id");
  const value = typeof id === "number" && Number.isFinite(id) ? String(id) : typeof id === "string" && id.trim() && !/[\u0000-\u001f\u007f]/.test(id) ? id : "";
  return route && value ? route + encodeURIComponent(value) : null;
}

// Plan-level states of the older nested shape ({activity:{…}, state|status}).
const PLAN_STATE = {
  registered: "registered", confirmed: "confirmed", requested: "requested", declined: "declined", withdrawn: "withdrawn",
  cancelled: "cancelled", activity_cancelled: "activity_cancelled",
  angefragt: "requested", "bestätigt": "confirmed", bestaetigt: "confirmed", abgelehnt: "declined", abgesagt: "activity_cancelled",
  waitlisted: "waitlisted", warteliste: "waitlisted", angeboten: "offered", storniert: "cancelled",
};

function nestedState(p, a) {
  if (isActivityCancelled(a)) return "activity_cancelled";
  const raw = own(p, "state") ?? own(p, "status");
  if (raw === undefined || raw === null) return participationState(a);
  return lookup(PLAN_STATE, raw) || "unknown";
}

// GET /member/plans lists own participations as flat activities without joined: there an explicit "bestätigt" counts.
// joined present (false/null) still contradicts it; cancellation wins; GET /activities keeps the strict participationState.
const CONFIRMED = { "bestätigt": true, bestaetigt: true, confirmed: true };

function flatPlanState(p) {
  if (isActivityCancelled(p)) return "activity_cancelled";
  if (!hasOwn(p, "joined") && lookup(CONFIRMED, own(p, "participation")) === true) return "confirmed";
  return participationState(p);
}

// Current Core: flat activities with participation; older nested shapes stay supported. Unknown never becomes registered.
export function normalizePlans(d) {
  const list = firstList(d, ["plans", "activities", "items", "entries"]);
  if (!list) return null;
  return list.filter(isObj).map((p) => {
    const nested = isObj(own(p, "activity"));
    const a = nested ? p.activity : p;
    const activityId = own(p, "activity_id") ?? (nested ? own(a, "id") : own(p, "id")) ?? null;
    let state = nested ? nestedState(p, a) : flatPlanState(p);
    if (state === "none") state = "unknown";
    const id = nested ? own(p, "id") ?? activityId : activityId;
    return {
      id: id === null || id === undefined ? "" : String(id),
      activityId: activityId === null || activityId === undefined ? null : String(activityId),
      title: text(own(p, "title") ?? own(a, "title")) || "Ohne Titel",
      date: own(p, "date") ?? own(p, "starts_at") ?? own(a, "date") ?? null,
      dateLocal: own(p, "date_local") ?? own(a, "date_local") ?? null,
      start: own(p, "start") ?? own(a, "start") ?? null,
      timezone: own(p, "timezone") ?? own(a, "timezone") ?? null,
      meetingPoint: own(p, "meeting_point") ?? own(a, "meeting_point") ?? "",
      location: own(p, "location") ?? own(a, "location") ?? "",
      state, known: state !== "unknown",
    };
  }).filter((p) => p.id);
}

// An incomplete collection is unavailable, never a successful empty result.
// Validate identity before exposing rows or their save/read actions.
const collectionId = (v) => typeof v === "number" && Number.isFinite(v)
  ? String(v) : typeof v === "string" && v.trim() && !/[\u0000-\u001f\u007f]/.test(v) ? v : null;

function completeCollection(list, project) {
  if (!list) return null;
  const seen = new Set();
  const rows = [];
  for (const entry of list) {
    if (!isObj(entry)) return null;
    const row = project(entry);
    const id = row && collectionId(own(row, "id"));
    if (id === null || seen.has(id)) return null;
    seen.add(id);
    rows.push(row);
  }
  return rows;
}

export function normalizeSaved(d) {
  const list = firstList(d, ["activities", "saved", "saved_activities", "items"]);
  return completeCollection(list, (x) => {
    const a = hasOwn(x, "activity") ? own(x, "activity") : x;
    if (!isObj(a)) return null;
    return { ...a, id: own(a, "id") ?? own(x, "activity_id"), savedAt: own(x, "saved_at") || null };
  });
}

const KINDS = [["contact", "Kontakte"], ["kontakt", "Kontakte"], ["activ", "Aktivitäten"], ["aktiv", "Aktivitäten"], ["group", "Kreise"], ["kreis", "Kreise"], ["message", "Nachrichten"], ["nachricht", "Nachrichten"], ["pass", "Access Pass"]];

export function notificationKind(kind) {
  const k = String(kind || "").toLowerCase();
  const hit = KINDS.find(([p]) => k.includes(p));
  return hit ? hit[1] : "Allgemein";
}

export function normalizeNotifications(d) {
  const list = firstList(d, ["notifications", "items"]);
  return completeCollection(list, (n) => ({
    id: collectionId(own(n, "id")),
    kind: notificationKind(n.type || n.kind),
    title: text(n.title) || "Benachrichtigung",
    body: text(n.body || n.text),
    createdAt: n.created_at || null,
    read: n.read === true || Boolean(n.read_at),
    link: refLink(own(n, "ref")) || internalLink(own(n, "link") || own(n, "url")),
  }));
}

const PASS_STATE = { angefragt: "Angefragt", requested: "Angefragt", freigegeben: "Freigegeben", granted: "Freigegeben", abgelehnt: "Abgelehnt", declined: "Abgelehnt", abgelaufen: "Abgelaufen", expired: "Abgelaufen", widerrufen: "Widerrufen", revoked: "Widerrufen" };

// Person who asked: the Core's own recipient_name first, older requested_by only as fallback; nothing invented.
const personText = (v) => (typeof v === "string" && v.trim() ? v.trim() : "");

function requesterOf(r) {
  const by = own(r, "requested_by");
  return personText(own(r, "recipient_name")) || personText(own(by, "name")) || personText(own(by, "role")) || "Empfänger nicht angegeben";
}

// Pass fields of the Core 1.1 members collection; used when a request or grant brings no own field_labels.
export const CORE_PASS_LABELS = { pass_number: "Ausweisnummer", category: "Kategorie", valid_until: "Gültig bis", benefits: "Vergünstigungen", issued_by: "Ausgestellt von" };

const fieldText = (labels, f) => {
  const l = own(labels, f);
  return typeof l === "string" && l.trim() ? l.trim() : own(CORE_PASS_LABELS, f) || String(f);
};

export function normalizePassRequests(d) {
  const list = firstList(d, ["requests", "items"]);
  if (!list) return null;
  return list.filter((r) => r && r.id !== undefined).map((r) => {
    const labels = isObj(r.field_labels) ? r.field_labels : {};
    return {
      id: String(r.id),
      state: PASS_STATE[String(r.state || r.status || "").toLowerCase()] || "Unbekannt",
      purpose: text(r.purpose || r.request_purpose) || "Kein Zweck angegeben",
      fields: (Array.isArray(r.fields) ? r.fields : []).map((f) => fieldText(labels, f)),
      expiresAt: r.expires_at || null,
      days: Number.isFinite(Number(r.days)) ? Number(r.days) : null,
      requester: requesterOf(r),
      createdAt: r.created_at || null,
    };
  });
}
